import React, { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { LOST_REASONS } from "@/lib/stages";
import { cn } from "@/lib/utils";

export function LostDialog({ opportunity, open, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/opportunities/${opportunity.id}/lost/suggest`);
      setSuggestion(r.data);
      if (LOST_REASONS.includes(r.data.reason)) setReason(r.data.reason);
      if (r.data.explanation && !note) setNote(r.data.explanation);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSuggesting(false);
    }
  };

  const confirm = async () => {
    if (!reason) return toast.error("Pick a reason first");
    setBusy(true);
    try {
      await api.post(`/opportunities/${opportunity.id}/lost`, { reason, note });
      toast.success("Marked as Lost");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="lost-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Why did we lose?</DialogTitle>
          <DialogDescription>{opportunity.account_name} – {opportunity.name}. This feeds win/loss insights.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {LOST_REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(r)} data-testid={`lost-reason-${r.toLowerCase().replace(/\s/g, "-")}`} className={cn("rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors hover:bg-muted", reason === r && "border-primary bg-primary/[0.08] text-foreground")}>{r}</button>
          ))}
        </div>
        {suggestion ? (
          <div className="ai-banner text-sm" data-testid="lost-ai-suggestion">
            <span className="font-medium">AI suggests «{suggestion.reason}»</span> ({Math.round((suggestion.confidence || 0) * 100)}% confidence). {suggestion.explanation}
            {suggestion.learning ? <div className="mt-1 text-muted-foreground">Learning: {suggestion.learning}</div> : null}
          </div>
        ) : (
          <Button type="button" variant="outline" onClick={suggest} disabled={suggesting} className="gap-2 self-start" data-testid="lost-ai-suggest-button"><Sparkles className={cn("h-4 w-4 text-primary", suggesting && "animate-pulse")} />{suggesting ? "Analysing history..." : "Suggest reason from history"}</Button>
        )}
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What did we learn? (optional)" className="rounded-xl" data-testid="lost-note-textarea" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={confirm} disabled={busy || !reason} variant="destructive" data-testid="lost-confirm-button">{busy ? "Saving..." : "Mark as Lost"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
